import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Ship, Loader2, ArrowRight } from "lucide-react";

const HERO = "https://images.unsplash.com/photo-1606185540834-d6e7483ee1a4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxjYXJnbyUyMHNoaXAlMjBhZXJpYWwlMjBwb3J0fGVufDB8fHx8MTc4NzU3ODQ1NXww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email.trim(), password);
      nav(u.must_change_password ? "/change-password" : "/");
    } catch (err) {
      setError(apiErr(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left: form */}
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12 relative">
        <div className="max-w-sm w-full mx-auto">
          <div className="flex items-center gap-2 mb-12">
            <div className="h-10 w-10 bg-primary text-primary-foreground flex items-center justify-center rounded-sm">
              <Ship className="h-5 w-5" strokeWidth={2.2} />
            </div>
            <div className="leading-tight">
              <p className="font-display font-black tracking-tight text-xl">VEAIMPEX</p>
              <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">VIPL Operations</p>
            </div>
          </div>

          <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground mb-2">Staff Portal</p>
          <h1 className="text-4xl font-display font-black tracking-tighter mb-2">Sign in.</h1>
          <p className="text-sm text-muted-foreground mb-8">
            Access your customers, tasks and weekly follow-up tracker.
          </p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label htmlFor="email" className="text-xs font-bold uppercase tracking-wide">Email ID</Label>
              <Input
                id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="name@veaimpex.co.in" required autoComplete="username"
                className="mt-1.5 rounded-sm h-11" data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs font-bold uppercase tracking-wide">Password</Label>
              <Input
                id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" required autoComplete="current-password"
                className="mt-1.5 rounded-sm h-11" data-testid="login-password-input"
              />
            </div>
            {error && (
              <p className="text-sm text-destructive font-medium border border-destructive/30 bg-destructive/5 px-3 py-2 rounded-sm" data-testid="login-error">
                {error}
              </p>
            )}
            <Button
              type="submit" disabled={loading}
              className="w-full h-11 rounded-sm font-bold uppercase tracking-wide gap-2 hover:hard-shadow-sm transition-all"
              data-testid="login-submit-button"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign in <ArrowRight className="h-4 w-4" /></>}
            </Button>
          </form>

          <p className="text-xs text-muted-foreground mt-8 border-t border-border pt-4">
            First time? Use your company email and the password provided by admin. You'll be asked to set a new password.
          </p>
        </div>
      </div>

      {/* Right: hero */}
      <div className="hidden lg:block relative">
        <img src={HERO} alt="Cargo port" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-primary/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute bottom-0 left-0 p-12 text-white">
          <p className="text-xs font-bold uppercase tracking-[0.25em] mb-3 opacity-90">Import · Export · Pharma Trade</p>
          <h2 className="text-4xl font-display font-black tracking-tighter max-w-md leading-tight">
            One workspace for every customer, order and follow-up.
          </h2>
        </div>
      </div>
    </div>
  );
}
